/**
 * @file     myReadDisplay.ts
 * @purpose  Pure display math for the Read panel: per-component point
 *           contribution, lens-aware score interval, and the trust line.
 *           Kept free of React/MUI so the math is unit-testable and the
 *           numbers shown always reconcile with the score chip.
 */
import type { ScoreBreakdown } from '@/services/deal-finder/types';
import { computeScoreInterval, type ScoreInterval } from '@/services/deal-finder/engine/confidence';

/**
 * Points a component contributes to the /100 composite under the active
 * lens. `weight` is a fraction (e.g. 0.22), `score` is 0-100, so the
 * contribution is simply score × weight (max = weight × 100 points).
 */
export function contributionPts(score: number, weight: number): number {
  return score * weight;
}

/** 95% CI on the lensed composite, from the same breakdown + weights the panel renders. */
export function lensScoreInterval(
  breakdown: ScoreBreakdown,
  weights: Partial<Record<keyof ScoreBreakdown, number>>,
  visibleKeys: ReadonlyArray<keyof ScoreBreakdown>,
  centerScore: number,
): ScoreInterval | null {
  const components = visibleKeys.map((k) => ({
    rawWeight: weights[k] ?? 0,
    score: breakdown[k]?.score ?? null,
  }));
  return computeScoreInterval(components, centerScore);
}

/**
 * Trust line under the score chip. Counts populated signals from the same
 * breakdown the panel displays and ranges the score with the 95% CI so the
 * coverage claim and the number can never disagree.
 */
export function trustLineText(
  breakdown: ScoreBreakdown,
  visibleKeys: ReadonlyArray<keyof ScoreBreakdown>,
  interval: ScoreInterval | null,
): string {
  const total = visibleKeys.length;
  const populated = visibleKeys.filter((k) => breakdown[k]?.score != null).length;
  const base = `Based on ${populated} of ${total} signals`;
  if (!interval) return base;
  return `${base} · score likely ${interval.low} to ${interval.high}`;
}
